import { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE, VIEWPORT } from '../../utils/animations';

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Vertical offset (px) the element rises from. */
  y?: number;
  /** Delay in seconds before the reveal starts. */
  delay?: number;
}

/** Fades + lifts a single element into view on scroll. Respects reduced-motion. */
export const Reveal = ({ children, className, y = 24, delay = 0 }: RevealProps) => {
  const reduce = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEWPORT}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
};

interface StaggerProps {
  children: ReactNode;
  className?: string;
  stagger?: number;
}

/** Container that staggers its <StaggerItem> children into view. */
export const Stagger = ({ children, className, stagger = 0.1 }: StaggerProps) => {
  const reduce = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={reduce ? false : 'hidden'}
      whileInView="visible"
      viewport={VIEWPORT}
      variants={{ visible: { transition: { staggerChildren: stagger } } }}
    >
      {children}
    </motion.div>
  );
};

interface StaggerItemProps {
  children: ReactNode;
  className?: string;
  y?: number;
}

export const StaggerItem = ({ children, className, y = 24 }: StaggerItemProps) => {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  );
};
